import { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import RichTextEditor from '../../../../common/components/RichTextEditor/RichTextEditor';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { marked } from 'marked';
import { editContent, updateDraftStatus, VersionData } from '../../IdeationService';
import ShowToast from '../../../../common/components/ShowToast';
import { ToastTypeEnum } from '../../../../constants/solutionXPLR';

import { KeyboardArrowDownOutlined as KeyboardArrowDownOutlinedIcon } from '@mui/icons-material';
import { fieldAPIKeyMap } from '../../../NewIdeationXPLR/shared/constants';
import CircularLoader from '../../../../common/components/SpinnerLoader';
import BpmnViewer from './BpmnViewer';
import ConfirmationModal from '../../../../common/components/ConfirmationModal';
import { useNavigationGuard } from '../../../../common/hooks/useNavigationGuard';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VersionDataItem {
  version: string;
  label: string;
}

interface IceScore {
  ice_score: string | number;
  impact: string | number;
  confidence: string | number;
  ease: string | number;
}

interface UseCaseDetails {
  [key: string]: string | number | boolean | Array<string | number> | IceScore;
}

interface CoiUseCaseProps {
  useCaseId?: string;
  selectedVersion?: string;
  onVersionChange?: (version: string) => void;
  versionData: VersionData;
  useCaseDetails: UseCaseDetails;
  updateChats?: () => void;
  isSending?: boolean;
  field: string;
  setActiveStep: (step: string) => void;
  fileSummary: string;
  editorContent: string;
  setEditorContent: (content: string) => void;
  isSendingToSolutionXPLR: boolean;
  setIsSendingToSolutionXPLR: (value: boolean) => void;
  handleSendUseCaseData: () => void;
  reportLoading?: boolean;
  generateDiagramXml: () => Promise<void>;
  diagramXml: string;
  isDiagramLoading: boolean;
  diagramStatus?: string;
  initialEditMode?: boolean;
  diagramVersion?: string;
}

type ActiveTab = 'description' | 'diagram';

// ─── Component ────────────────────────────────────────────────────────────────

const CoiUseCase = ({
  useCaseId = '',
  selectedVersion = '',
  versionData,
  useCaseDetails,
  updateChats,
  isSending = false,
  field,
  onVersionChange,
  fileSummary,
  editorContent,
  setEditorContent,
  reportLoading,
  generateDiagramXml,
  diagramXml = '',
  isDiagramLoading = false,
  diagramStatus = '',
  initialEditMode = false,
  diagramVersion = '',
}: CoiUseCaseProps) => {
  const [currentVersion, setCurrentVersion] = useState<string>(selectedVersion);
  const solutionData = useSelector((state: RootState) => state?.solutionXPLR);
  const [isEditing, setIsEditing] = useState<boolean>(initialEditMode);
  const [prevVersionDataLength, setPrevVersionDataLength] = useState<number>(0);
  const [isVersionDataUpdating, setIsVersionDataUpdating] = useState<boolean>(false);
  const [, setUseCaseDetails] = useState<UseCaseDetails>(useCaseDetails);
  const [activeTab, setActiveTab] = useState<ActiveTab>('description');
  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false);
  const [hasEdited, setHasEdited] = useState<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(false);
  const { isModalOpen, confirmNavigation, cancelNavigation } = useNavigationGuard({
    shouldBlock: isEditing && hasEdited,
  });

  const diagramEnabledFields = [
    'currentProcess',
    'Current Process',
    'expectedImpact',
    'Expected Impact',
    'specificIdeasOfImprovement',
    'Specific Ideas of Improvement',
    'keyPerformanceIndicators',
    'Key Performance Indicators',
    'current_process_solutionxplr',
    'currentProcessSolutionXplr',
    'proposed_enhancements_solutionxplr',
    'proposedEnhancementsSolutionXplr',
    'estimated_impact_solutionxplr',
    'estimatedImpactSolutionXplr',
    'current_process',
    'expected_impact',
    'key_performance_indicator',
    'ideas_of_improvement',
  ];

  const isSelected = Boolean(selectedVersion);
  const versionedContent: VersionDataItem[] = Object.keys(versionData).map((version) => ({
    version,
    label: `v${version?.split('.')?.[2] || ''}`,
  }));

  const latestVersion = versionedContent[versionedContent.length - 1];

  const setHtmlContent = async (data: string = '') => {
    const html = await marked.parse(data);
    setEditorContent(html);
  };
  // ── Sync version prop ──
  useEffect(() => {
    setCurrentVersion(selectedVersion);
    setHtmlContent(versionData?.[selectedVersion?.toString()]?.summary || fileSummary || '');
  }, [selectedVersion, versionData]);

  // ── Version data updating state ──
  useEffect(() => {
    const currentVersionDataLength = Object.keys(versionData || {}).length;
    if (isSending && currentVersionDataLength !== prevVersionDataLength) {
      setIsVersionDataUpdating(true);
    } else if (!isSending) {
      setTimeout(() => setIsVersionDataUpdating(false), 500);
    }
    setPrevVersionDataLength(currentVersionDataLength);
  }, [versionData, isSending, prevVersionDataLength]);

  // ── Handlers ──
  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    const version = event.target.value as string;
    setCurrentVersion(version);
    onVersionChange?.(version);
  };

  const handleTabChange = (tab: ActiveTab) => {
    if (tab !== 'description' && isEditing) {
      setHasEdited(false);
      isInitialLoadRef.current = false;
      setIsEditing(false);
      setHtmlContent(versionData?.[selectedVersion?.toString()]?.summary || fileSummary || '');
    }
    setActiveTab(tab);
  };

  const handleEditAndRestore = async (content: string, isRestore: boolean = false) => {
    try {
      const organizationId = localStorage.getItem('current_organization') || '';
      const payload = {
        query: {
          report: content,
          impact: useCaseDetails?.impact || '',
          user_input: '',
        },
        action: isRestore ? 'restore' : 'edit',
        version: currentVersion,
        tenantId: '6764029052789d00255f17a1',
        useCaseId,
        field_type: fieldAPIKeyMap[field],
        organization_id: organizationId,
      };

      await editContent(payload);

      if (!isRestore && useCaseDetails?.status === 'solutioning') {
        try {
          await updateDraftStatus(useCaseId, { status: 'report-generated' });
          setUseCaseDetails((prev: any) => ({ ...prev, status: 'report-generated' }));
          ShowToast({
            message: 'Report updated and status set to Report Generated',
            type: ToastTypeEnum.Success,
          });
        } catch (statusErr) {
          console.error('Error updating draft status:', statusErr);
        }
      }
      updateChats?.();
      ShowToast({ message: 'Content edited successfully', type: ToastTypeEnum.Success });
    } catch (error: unknown) {
      console.error('Error editing content:', error);
      ShowToast({ message: 'Failed to edit content', type: ToastTypeEnum.Error });
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      if (!editorContent) {
        ShowToast({
          message: 'Nothing to copy. Please add some content first.',
          type: ToastTypeEnum.Warn,
        });
        return;
      }
      const htmlBlob = new Blob([editorContent], { type: 'text/html' });
      const plainText = editorContent.replace(/<[^>]*>/g, '');
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
      ]);
      ShowToast({ message: 'Copied to clipboard successfully!', type: ToastTypeEnum.Success });
    } catch (error) {
      ShowToast({
        message: `Failed to copy text. Please try again.\nError: ${error}`,
        type: ToastTypeEnum.Error,
      });
    }
  };

  // ── Shared loading placeholder ──
  const renderLoadingState = (marginTop = '20px') => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100% - 45px)',
        flexDirection: 'column',
        gap: 2,
        mt: marginTop,
      }}
    >
      <img
        src="/images/Star_Loader.gif"
        alt="Loading"
        style={{ width: '200px', height: 'auto', maxWidth: '100%' }}
      />
      <Typography
        sx={{ fontSize: '16px', fontFamily: 'poppins', fontWeight: 600, color: '#757575', mt: -9 }}
      >
        Analysing the provided input
      </Typography>
    </Box>
  );

  // ── Tab Pill Button ──
  const TabButton = ({ tab, label }: { tab: ActiveTab; label: string }) => {
    const active = activeTab === tab;
    return (
      <Button
        disableRipple
        onClick={() => handleTabChange(tab)}
        sx={{
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          color: '#0A1F44',
          backgroundColor: 'transparent',

          border: active ? '1px solid #1A55F9' : '1px solid transparent',

          borderRadius: tab === 'description' ? '8px 0 0 8px' : '0 8px 8px 0',

          width: 100,
          justifyContent: 'center',
          flexShrink: 0,

          px: 1.5,
          py: '3px',
          lineHeight: '22px',

          '&:hover': {
            backgroundColor: '#F0F4FF',
          },
        }}
      >
        {label}
      </Button>
    );
  };

  // ─── Header ───────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px 12px 0 0',
        borderBottom: '1px solid #E1E1E1',
        backgroundColor: '#fff',
        pl: { xs: '12px', md: '15px' },
        pr: { xs: '12px', md: '12px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: '12px',
          pb: '12px',
          height: '29px',
          //  flexWrap: { xs: 'wrap', md: 'nowrap' },
          // rowGap: { xs: 1, md: 0 },
          // pt: '14px',
          // pb: '9px',
          // minHeight: '29px',
        }}
      >
        {/* Left: title + version selector + tabs */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              fontWeight: 600,
              fontSize: '15px',
              color: '#333333',
            }}
          >
            Semantic Knowledge
          </Typography>

          {/* Version selector — description tab only */}
          {activeTab === 'description' &&
            isSelected &&
            !isSending &&
            !isVersionDataUpdating &&
            !isEditing &&
            versionedContent?.length > 0 && (
              <FormControl
                size="small"
                sx={{
                  minWidth: 56,
                  background: '#E8EEFE',
                  borderRadius: '8px',
                  border: '1px solid #B9B9B9',
                  maxHeight: '28px',
                }}
              >
                <Select
                  value={currentVersion}
                  onChange={handleVersionChange}
                  IconComponent={KeyboardArrowDownOutlinedIcon}
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    maxHeight: '30px',
                  }}
                >
                  {versionedContent.map((v) => (
                    <MenuItem
                      key={v.version}
                      value={v.version}
                      sx={{ fontSize: '13px', fontWeight: 600 }}
                    >
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {/* Edit mode label */}
          {activeTab === 'description' && isEditing && (
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: '#E8EEFE',
                border: '1px solid #B9B9B9',
                borderRadius: '8px',
                height: '30px',
                fontSize: '14px',
                padding: '0 10px',
              }}
            >
              Edit Mode
            </Typography>
          )}
        </Box>

        {/* Tab pills */}
        {diagramEnabledFields?.includes(field) && (
          <Box sx={{ display: 'flex', border: '1px solid #D7E0FF', borderRadius: '8px' }}>
            <TabButton tab="description" label="Description" />
            <TabButton tab="diagram" label="Diagram" />
          </Box>
        )}

        {/* Right: action icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {activeTab === 'description' && !isEditing && !isSending && !isVersionDataUpdating && (
            <>
              {Object.keys(versionData || {})?.length > 0 &&
                (currentVersion === latestVersion?.version ? (
                  <IconButton
                    onClick={() => {
                      setIsEditing(true);
                      setHasEdited(false);
                      isInitialLoadRef.current = true;
                    }}
                    disabled={!isSelected}
                  >
                    <img src="/images/edit_icon_coi.svg" alt="edit" />
                  </IconButton>
                ) : (
                  selectedVersion &&
                  currentVersion !== latestVersion?.version && (
                    <Button
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        mr: 1,
                        background: '#EEF5FF',
                        borderRadius: '8px',
                        border: 'none',
                      }}
                      onClick={() => {
                        handleEditAndRestore(editorContent, true);
                        setIsEditing(false);
                      }}
                    >
                      Restore this Version
                    </Button>
                  )
                ))}
              {editorContent?.length > 0 && (
                <IconButton disabled={!isSelected} onClick={handleCopyToClipboard}>
                  <img src="/images/copy-icon.svg" alt="copy" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );

  // ─── Empty / waiting state (no session selected yet) ─────────────────────
  if (!isSelected) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {renderHeader()}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            gap: 2,
          }}
        >
          {isSending || isVersionDataUpdating ? (
            <>
              <img
                src="/images/Star_Loader.gif"
                alt="Loading"
                style={{ width: '200px', height: 'auto', maxWidth: '100%' }}
              />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                  color: '#757575',
                  mt: -4,
                }}
              >
                Analysing the provided input
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <img
                src="/images/star-static.svg"
                alt="Select a use case"
                style={{ height: 'auto', maxWidth: '100%' }}
              />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'poppins',
                  fontWeight: 600,
                  color: '#757575',
                  mt: -2,
                  mr: -2,
                }}
              >
                {reportLoading ? 'Loading...' : 'waiting for user input'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ─── Main layout (session selected) ──────────────────────────────────────
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {renderHeader()}

      {/* Content */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 0,
          pl: '0px',
          backgroundColor: '#ffffff',
          borderRadius: '0 0 12px 12px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '3px' },
        }}
      >
        {/* ── DESCRIPTION TAB ── */}
        {activeTab === 'description' &&
          (() => {
            const shouldShowLoader =
              isSending || isVersionDataUpdating || (isSelected && !editorContent);
            if (shouldShowLoader) return renderLoadingState();

            return (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <RichTextEditor
                  key={`version-${currentVersion}`}
                  content={editorContent}
                  onChange={(content) => {
                    setEditorContent(content);
                    if (isEditing) {
                      if (isInitialLoadRef.current) {
                        isInitialLoadRef.current = false;
                      } else {
                        setHasEdited(true);
                      }
                    }
                  }}
                  height="100%"
                  width="100%"
                  maxHeight="100vh"
                  readOnly={!isEditing}
                  toolbarOptions={{
                    link: false,
                    image: false,
                    codeBlock: false,
                    textColor: false,
                    fontFamily: false,
                  }}
                  solutionId={solutionData?.id}
                  orgId={solutionData?.id || ''}
                  processStatus={'ELABORATED_SUMMARY_GENERATED'}
                  subProcessStatus={''}
                  enableComments={false}
                  disableHover={true}
                />
              </Box>
            );
          })()}

        {/* ── DIAGRAM TAB ── */}
        {activeTab === 'diagram' &&
          (() => {
            if (isSending || isVersionDataUpdating) return renderLoadingState();
            const isInProgress =
              diagramStatus === 'in_progress' || diagramStatus === 'Processing' || isDiagramLoading;
            const isCompleted = diagramStatus === 'completed' || diagramStatus === 'Completed';

            if (isInProgress) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <CircularLoader />
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontFamily: 'poppins',
                      fontWeight: 400,
                      color: '#757575',
                      maxWidth: '340px',
                      textAlign: 'center',
                      mt: '12px',
                    }}
                  >
                    Analyzing documents and creating your process diagram. Please wait...
                  </Typography>
                </Box>
              );
            }

            if (!isCompleted || !diagramXml) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <img
                    src="/images/generate-image-placeholder.svg"
                    alt="No diagram"
                    style={{ height: 'auto', maxWidth: '100%' }}
                  />
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontFamily: 'poppins',
                      fontWeight: 400,
                      color: '#757575',
                      maxWidth: '340px',
                      textAlign: 'center',
                      mt: '30px',
                      mb: '24px',
                    }}
                  >
                    Generate a visual diagram on demand from the provided description.
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      background: '#fff',
                      color: '#1A55F9',
                      borderRadius: '6px',
                      border: '1px solid #1A55F9',
                      fontSize: '16px',
                      fontWeight: 500,
                      ':hover': {
                        backgroundColor: '#1A55F9',
                        color: '#fff',
                      },
                    }}
                    onClick={() => {
                      generateDiagramXml();
                    }}
                    disabled={isDiagramLoading || isInProgress}
                  >
                    Generate Diagram
                  </Button>
                </Box>
              );
            }

            const showRegenerateBar =
              diagramXml &&
              currentVersion === latestVersion?.version &&
              isCompleted &&
              diagramVersion !== latestVersion.version
                ? true
                : false;

            return (
              <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                <BpmnViewer
                  xml={diagramXml}
                  showRegenerateBar={showRegenerateBar}
                  onRegenerate={generateDiagramXml}
                />
              </Box>
            );
          })()}
      </Paper>

      {/* Save / Discard bar — description edit mode only */}
      {activeTab === 'description' && isEditing && (
        <Box
          sx={{
            bottom: 0,
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              mr: 2,
              mt: 0.75,
              background: '#FFFFFFF',
              borderRadius: '8px',
              border: '1px solid #1A55F9',
            }}
            onClick={() => {
              if (hasEdited) {
                setShowDiscardModal(true);
              } else {
                setIsEditing(false);
                setHtmlContent(
                  versionData?.[selectedVersion?.toString()]?.summary || fileSummary || ''
                );
              }
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: 'none',
              mr: 2,
              mt: 0.75,
              background: '#1B54F8',
              color: 'white',
              borderRadius: '8px',
              border: 'none',
            }}
            onClick={() => {
              handleEditAndRestore(editorContent, false);
              setIsEditing(false);
            }}
            disabled={!hasEdited}
          >
            Save
          </Button>
        </Box>
      )}
      {showDiscardModal && (
        <ConfirmationModal
          isOpen={showDiscardModal}
          title="Discard Changes"
          description="You have unsaved changes. Are you sure you want to discard them?"
          onPrimaryAction={() => {
            setShowDiscardModal(false);
          }}
          onClose={() => setShowDiscardModal(false)}
          primaryButtonText="Keep Editing"
          secondaryButtonText="Discard Changes"
          onSecondaryAction={() => {
            setHasEdited(false);
            isInitialLoadRef.current = false;
            setShowDiscardModal(false);
            setIsEditing(false);
            setHtmlContent(
              versionData?.[selectedVersion?.toString()]?.summary || fileSummary || ''
            );
          }}
        />
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave?"
        primaryButtonText="Continue"
        secondaryButtonText="Stay"
        onPrimaryAction={confirmNavigation}
        onSecondaryAction={cancelNavigation}
        onClose={cancelNavigation}
      />
    </Box>
  );
};

export default CoiUseCase;
